/**
 * The simulator's right rail: the run mode on top, then the scenario's world as sections
 * read outside-in (the conversation, who wrote it, what they write about), with Run details
 * pinned to the bottom. Open sections live in an atom, so loading a case can expand exactly
 * the ones it filled.
 */

import { useAtom, useAtomValue } from 'jotai';
import { UserRound, MessagesSquare, Sparkles, Package, Repeat, ShoppingBag, ChevronsDownUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MODE_OPTIONS } from '@/modules/back-testing/runs/modes';
import { Segmented } from '../ui/segmented';
import { CollapsibleSection, PanelHeader } from './panel';
import { ConversationDetails } from './conversation-details';
import { type CustomerDetails } from './customer-details';
import {
  CustomerProfileSection,
  OrderProfileSection,
  ProductProfileSection,
  SubscriptionProfileSection,
} from './simulation-profile';
import {
  highlightedSectionsAtom,
  openSectionsAtom,
  sectionConfigured,
  type SimulationProfileExtras,
} from './simulation-profile.store';
import { RunDetailsContent } from './run-details-card';
import type { LoadedThread } from '../conversations/loaded-thread';
import type { RunDetails, RunApprovalTool } from '@/stores/websocket.store';

/** The configuration sections in render order; Live data reads its world from live systems, so it shows only the conversation. */
const LIVE_DATA_SECTION_IDS = ['conversation'];
const ALL_SECTION_IDS = [...LIVE_DATA_SECTION_IDS, 'customer', 'order', 'subscription', 'product'];

interface SimulatorSidebarProps {
  details: RunDetails | null;
  loading?: boolean;
  error?: string | null;
  approval?: RunApprovalTool[] | null;
  customer: CustomerDetails;
  onCustomerChange: (value: CustomerDetails) => void;
  profile: SimulationProfileExtras;
  onProfileChange: (value: SimulationProfileExtras) => void;
  simulateTools: boolean;
  onSimulateToolsChange: (value: boolean) => void;
  /** Why the tool mode cannot be switched, while it cannot. */
  toolModeLock?: string | null;
  loadedThread: LoadedThread | null;
  /** The past ticket behind the loaded scenario or the reopened run, when it has one. */
  pastTicketId: string | null;
  /**
   * Changes whenever another case is put on screen. The sections key off it, so
   * a switch drops the form state they hold locally (which one is editing, which
   * reveal pills are open) instead of showing the previous case's behind them.
   */
  caseKey: string;
  onOpenPicker: () => void;
  onLoadTicketId: (ticketId: string) => Promise<boolean>;
}

export const SimulatorSidebar = ({
  details,
  loading,
  error,
  approval,
  customer,
  onCustomerChange,
  profile,
  onProfileChange,
  simulateTools,
  onSimulateToolsChange,
  toolModeLock = null,
  loadedThread,
  pastTicketId,
  caseKey,
  onOpenPicker,
  onLoadTicketId,
}: SimulatorSidebarProps) => {
  const [openSections, setOpenSections] = useAtom(openSectionsAtom);
  const highlighted = useAtomValue(highlightedSectionsAtom);

  const section = (id: string) => ({
    open: openSections.includes(id),
    highlighted: highlighted.includes(id),
    onOpenChange: (open: boolean) =>
      setOpenSections((prev) => (open ? [...prev, id] : prev.filter((s) => s !== id))),
  });

  // Only the sections on screen: in Live data the world sections are not rendered.
  const sectionIds = simulateTools ? ALL_SECTION_IDS : LIVE_DATA_SECTION_IDS;
  const allOpen = sectionIds.every((id) => openSections.includes(id));
  const toggleAll = () =>
    // Sections outside this list (Run details) keep their state.
    setOpenSections((prev) =>
      allOpen ? prev.filter((id) => !sectionIds.includes(id)) : [...new Set([...prev, ...sectionIds])]
    );

  return (
    // Run details is output, not configuration, so it sits pinned below the
    // scrolling configuration sections rather than among them.
    <div className='flex h-full flex-col border-l border-gray-500 bg-white'>
      <div className='shrink-0 border-b border-gray-500'>
        <PanelHeader
          title='Tools'
          description={
            <>
              {simulateTools
                ? 'Tools answer from the world configured below, bent to the loaded scenario’s type. Writes are simulated.'
                : 'Reads come from this account’s connected tools, bent to the loaded scenario’s type. Writes are simulated, never sent.'}
              {toolModeLock && <span className='mt-1 block text-amber-700'>{toolModeLock}.</span>}
            </>
          }
          trailing={
            <Segmented
              options={MODE_OPTIONS}
              value={simulateTools ? 'simulated' : 'grounded'}
              onChange={(mode) => onSimulateToolsChange(mode === 'simulated')}
              disabled={Boolean(toolModeLock)}
            />
          }
        />
      </div>

      <div className='flex shrink-0 items-center justify-end border-b border-gray-500 px-4 py-1.5'>
        <button
          type='button'
          onClick={toggleAll}
          className='flex items-center gap-1 text-[11px] leading-4 text-gray-800 transition-colors hover:text-text-heading'
        >
          <ChevronsDownUp className={cn('size-3.5 transition-transform duration-300', !allOpen && 'rotate-180')} />
          {allOpen ? 'Collapse all' : 'Expand all'}
        </button>
      </div>

      <div className='flex min-h-0 flex-1 flex-col overflow-y-auto'>
        <CollapsibleSection
          icon={MessagesSquare}
          title='Conversation Details'
          // A loaded thread or a replayed ticket: the section holds something a collapsed header would hide.
          configured={Boolean(loadedThread || pastTicketId)}
          {...section('conversation')}
        >
          <ConversationDetails
            key={caseKey}
            loaded={loadedThread}
            pastTicketId={pastTicketId}
            onOpenPicker={onOpenPicker}
            onLoadTicketId={onLoadTicketId}
          />
        </CollapsibleSection>

        {simulateTools && (
          <>
            <CollapsibleSection
              icon={UserRound}
              title='Customer Details'
              configured={sectionConfigured(profile, 'customer')}
              {...section('customer')}
            >
              <CustomerProfileSection
                key={caseKey}
                customer={customer}
                onCustomerChange={onCustomerChange}
                extras={profile}
                onExtrasChange={onProfileChange}
              />
            </CollapsibleSection>

            <CollapsibleSection
              icon={Package}
              title='Order Details'
              configured={sectionConfigured(profile, 'order')}
              {...section('order')}
            >
              <OrderProfileSection key={caseKey} value={profile} onChange={onProfileChange} />
            </CollapsibleSection>

            <CollapsibleSection
              icon={Repeat}
              title='Subscription Details'
              configured={sectionConfigured(profile, 'subscription')}
              {...section('subscription')}
            >
              <SubscriptionProfileSection key={caseKey} value={profile} onChange={onProfileChange} />
            </CollapsibleSection>

            <CollapsibleSection
              icon={ShoppingBag}
              title='Product Details'
              configured={sectionConfigured(profile, 'product')}
              {...section('product')}
            >
              <ProductProfileSection key={caseKey} value={profile} onChange={onProfileChange} />
            </CollapsibleSection>
          </>
        )}
      </div>

      <div className='shrink-0 border-t border-gray-500'>
        <CollapsibleSection icon={Sparkles} title='Run Details' className='border-b-0' {...section('run')}>
          <div className='-mr-1 max-h-[42vh] overflow-y-auto pr-1'>
            <RunDetailsContent details={details} loading={loading} error={error} approval={approval} />
          </div>
        </CollapsibleSection>
      </div>
    </div>
  );
};
